import React from "react";
import { Accordion, Checkbox, Icon } from "semantic-ui-react";
import { FormattedMessage, useIntl } from "react-intl";
import styles from "./FilterSidebar.module.scss";
import { categories, clients, fabrics, sizes } from "../../../utils/siteValues";
import { PatternDifficulty } from "../../../types/siteTypes";

const DIFFICULTY_OPTIONS: PatternDifficulty[] = ["easy", "medium", "hard"];

type Props = {
    searchParams?: URLSearchParams;
    setSearchParams?: (params: URLSearchParams) => void;
    selectedCategories: string[];
    setSelectedCategories: (categories: string[]) => void;
    selectedClients: string[];
    setSelectedClients: (clients: string[]) => void;
    selectedFabrics: string[];
    setSelectedFabrics: (fabrics: string[]) => void;
    selectedSizes: string[];
    setSelectedSizes: (sizes: string[]) => void;
    selectedDifficulty?: PatternDifficulty | null;
    setSelectedDifficulty?: (difficulty: PatternDifficulty | null) => void;
    resultsCount?: number | null;
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    searchPlaceholder?: string;
    isSticky?: boolean;
    showHeader?: boolean;
};

const FilterSidebar: React.FC<Props> = ({
    searchParams,
    setSearchParams,
    selectedCategories,
    setSelectedCategories,
    selectedClients,
    setSelectedClients,
    selectedFabrics,
    setSelectedFabrics,
    selectedSizes,
    setSelectedSizes,
    selectedDifficulty = null,
    setSelectedDifficulty,
    resultsCount = null,
    searchValue = "",
    onSearchChange,
    searchPlaceholder = "",
    isSticky = true,
    showHeader = true,
}) => {
    const intl = useIntl();
    const hasDifficultyFilter = typeof setSelectedDifficulty === "function";
    const [activeIndex, setActiveIndex] = React.useState<number[]>(
        hasDifficultyFilter ? [0, 1, 2, 3, 4] : [0, 1, 2, 3]
    );

    const handleAccordionClick = (index: number) => {
        if (activeIndex.includes(index)) {
            setActiveIndex(activeIndex.filter((i) => i !== index));
        } else {
            setActiveIndex([...activeIndex, index]);
        }
    };

    const handleCheckboxChange = (
        value: string,
        selected: string[],
        setSelected: (values: string[]) => void
    ) => {
        if (selected.includes(value)) {
            setSelected(selected.filter((v) => v !== value));
        } else {
            setSelected([...selected, value]);
        }
    };

    const handleClearAll = () => {
        setSelectedCategories([]);
        setSelectedClients([]);
        setSelectedFabrics([]);
        setSelectedSizes([]);
        // Clear search params if they exist
        if (searchParams && setSearchParams) {
            searchParams.delete("categories");
            searchParams.delete("clients");
            searchParams.delete("fabric");
            searchParams.delete("size");
            searchParams.delete("difficulty");
            searchParams.delete("search");
            setSearchParams(searchParams);

        }
        if (setSelectedDifficulty) {
            setSelectedDifficulty(null);
        }
        if (onSearchChange) {
            onSearchChange("");
        }
    };

    const hasActiveFilters =
        selectedCategories.length > 0 ||
        selectedClients.length > 0 ||
        selectedFabrics.length > 0 ||
        selectedSizes.length > 0 ||
        !!selectedDifficulty ||
        searchValue.trim().length > 0;

    return (
        <div className={`${styles.filterSidebar} ${!isSticky ? styles.filterSidebarStatic : ""}`.trim()}>
            {showHeader && (
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <h3 className={styles.title}>
                            <FormattedMessage id="patterns.filters.title" defaultMessage="Filters" />
                        </h3>
                        {typeof resultsCount === "number" && (
                            <span className={styles.headerResult}>
                                <FormattedMessage
                                    id="patterns.filters.found"
                                    defaultMessage="Patterns: {count}"
                                    values={{ count: resultsCount }}
                                />
                            </span>
                        )}
                    </div>
                    {hasActiveFilters && (
                        <button className={styles.clearButton} onClick={handleClearAll}>
                            <FormattedMessage
                                id="patterns.filters.clearAll"
                                defaultMessage="Clear All"
                            />
                        </button>
                    )}
                </div>
            )}

            {onSearchChange && (
                <div className={styles.searchWrap}>
                    <input
                        type="text"
                        value={searchValue}
                        onChange={(e) => onSearchChange(e.target.value)}
                        placeholder={
                            searchPlaceholder ||
                            intl.formatMessage({
                                id: "patterns.search.placeholder",
                                defaultMessage: "Search patterns",
                            })
                        }
                        className={styles.searchInput}
                    />
                </div>
            )}

            <Accordion styled fluid className={styles.accordion}>
                {/* Categories */}
                <Accordion.Title
                    active={activeIndex.includes(0)}
                    index={0}
                    onClick={() => handleAccordionClick(0)}
                    className={styles.accordionTitle}
                >
                    <Icon name="dropdown" />
                    <FormattedMessage
                        id="categories.categories.title"
                        defaultMessage="Category"
                    />
                    {selectedCategories.length > 0 && (
                        <span className={styles.badge}>{selectedCategories.length}</span>
                    )}
                </Accordion.Title>
                <Accordion.Content active={activeIndex.includes(0)}>
                    <div className={styles.checkboxGroup}>
                        {categories.map((category) => (
                            <div key={category} className={styles.checkboxItem}>
                                <Checkbox
                                    label={intl.formatMessage({
                                        id: `categories.categories.${category}`,
                                        defaultMessage: category,
                                    })}
                                    checked={selectedCategories.includes(category)}
                                    onChange={() =>
                                        handleCheckboxChange(
                                            category,
                                            selectedCategories,
                                            setSelectedCategories
                                        )
                                    }
                                />
                            </div>
                        ))}
                    </div>
                </Accordion.Content>

                {/* Clients */}
                <Accordion.Title
                    active={activeIndex.includes(1)}
                    index={1}
                    onClick={() => handleAccordionClick(1)}
                    className={styles.accordionTitle}
                >
                    <Icon name="dropdown" />
                    <FormattedMessage
                        id="categories.clients.title"
                        defaultMessage="For Who"
                    />
                    {selectedClients.length > 0 && (
                        <span className={styles.badge}>{selectedClients.length}</span>
                    )}
                </Accordion.Title>
                <Accordion.Content active={activeIndex.includes(1)}>
                    <div className={styles.checkboxGroup}>
                        {clients.map((client) => (
                            <div key={client} className={styles.checkboxItem}>
                                <Checkbox
                                    label={intl.formatMessage({
                                        id: `categories.clients.${client}`,
                                        defaultMessage: client,
                                    })}
                                    checked={selectedClients.includes(client)}
                                    onChange={() =>
                                        handleCheckboxChange(client, selectedClients, setSelectedClients)
                                    }
                                />
                            </div>
                        ))}
                    </div>
                </Accordion.Content>

                {/* Fabrics */}
                <Accordion.Title
                    active={activeIndex.includes(2)}
                    index={2}
                    onClick={() => handleAccordionClick(2)}
                    className={styles.accordionTitle}
                >
                    <Icon name="dropdown" />
                    <FormattedMessage
                        id="categories.fabrics.title"
                        defaultMessage="Fabric"
                    />
                    {selectedFabrics.length > 0 && (
                        <span className={styles.badge}>{selectedFabrics.length}</span>
                    )}
                </Accordion.Title>
                <Accordion.Content active={activeIndex.includes(2)}>
                    <div className={styles.checkboxGroup}>
                        {fabrics.map((fabric) => (
                            <div key={fabric} className={styles.checkboxItem}>
                                <Checkbox
                                    label={intl.formatMessage({
                                        id: `categories.fabrics.${fabric}`,
                                        defaultMessage: fabric,
                                    })}
                                    checked={selectedFabrics.includes(fabric)}
                                    onChange={() =>
                                        handleCheckboxChange(fabric, selectedFabrics, setSelectedFabrics)
                                    }
                                />
                            </div>
                        ))}
                    </div>
                </Accordion.Content>

                {/* Sizes */}
                <Accordion.Title
                    active={activeIndex.includes(3)}
                    index={3}
                    onClick={() => handleAccordionClick(3)}
                    className={styles.accordionTitle}
                >
                    <Icon name="dropdown" />
                    <FormattedMessage
                        id="categories.sizes.title"
                        defaultMessage="Sizes"
                    />
                    {selectedSizes.length > 0 && (
                        <span className={styles.badge}>{selectedSizes.length}</span>
                    )}
                </Accordion.Title>
                <Accordion.Content active={activeIndex.includes(3)}>
                    <div className={styles.checkboxGroup}>
                        {sizes.map((size) => (
                            <div key={size} className={styles.checkboxItem}>
                                <Checkbox
                                    label={size.toUpperCase()}
                                    checked={selectedSizes.includes(size)}
                                    onChange={() =>
                                        handleCheckboxChange(size, selectedSizes, setSelectedSizes)
                                    }
                                />
                            </div>
                        ))}
                    </div>
                </Accordion.Content>

                {hasDifficultyFilter && (
                    <>
                        <Accordion.Title
                            active={activeIndex.includes(4)}
                            index={4}
                            onClick={() => handleAccordionClick(4)}
                            className={styles.accordionTitle}
                        >
                            <Icon name="dropdown" />
                            <FormattedMessage
                                id="pattern.difficulty.label"
                                defaultMessage="Difficulty level"
                            />
                            {selectedDifficulty && (
                                <span className={styles.badge}>1</span>
                            )}
                        </Accordion.Title>
                        <Accordion.Content active={activeIndex.includes(4)}>
                            <div className={styles.checkboxGroup}>
                                {DIFFICULTY_OPTIONS.map((difficulty) => (
                                    <div key={difficulty} className={styles.checkboxItem}>
                                        <Checkbox
                                            label={intl.formatMessage({
                                                id: `pattern.difficulty.${difficulty}`,
                                                defaultMessage: difficulty,
                                            })}
                                            checked={selectedDifficulty === difficulty}
                                            onChange={() =>
                                                setSelectedDifficulty?.(
                                                    selectedDifficulty === difficulty
                                                        ? null
                                                        : difficulty
                                                )
                                            }
                                        />
                                    </div>
                                ))}
                            </div>
                        </Accordion.Content>
                    </>
                )}
            </Accordion>
        </div>
    );
};

export default FilterSidebar;
